<template>
  <Modal
    :show="show"
    :title="isEditing ? $t('workspace.modal.editPhase') : $t('workspace.modal.createPhase')"
    size="xlarge"
    :close-on-backdrop-click="!submitting"
    :loading="submitting"
    no-padding
    @close="closeModal"
  >
    <form @submit.prevent="handleSubmit" class="phase-form">
        <!-- Phase Name -->
        <div class="form-group">
          <label for="phaseName" class="required">{{ $t('workspace.modal.phaseName') }}</label>
          <input
            id="phaseName"
            v-model="form.name"
            type="text"
            :placeholder="$t('workspace.modal.phaseNamePlaceholder')"
            required
            minlength="3"
            maxlength="100"
          />
          <span v-if="errors.name" class="error-message">{{ errors.name }}</span>
        </div>

        <!-- Description -->
        <div class="form-group">
          <label for="phaseDescription">{{ $t('workspace.modal.description') }}</label>
          <textarea
            id="phaseDescription"
            v-model="form.description"
            :placeholder="$t('workspace.modal.descriptionPlaceholder')"
            maxlength="500"
            rows="3"
          ></textarea>
        </div>

        <!-- Date Range -->
        <div class="form-row">
          <div class="form-group">
            <label for="phaseStartDate">{{ $t('workspace.modal.startDate') }}</label>
            <input
              id="phaseStartDate"
              v-model="form.plannedStartDate"
              type="date"
            />
          </div>
          <div class="form-group">
            <label for="phaseEndDate">{{ $t('workspace.modal.endDate') }}</label>
            <input
              id="phaseEndDate"
              v-model="form.plannedEndDate"
              type="date"
              :min="form.plannedStartDate"
            />
          </div>
        </div>

        <!-- Form Actions -->
        <div class="form-actions">
          <button type="button" class="btn btn-secondary" @click="closeModal">
            {{ $t('common.cancel') }}
          </button>
          <button type="submit" class="btn btn-primary" :disabled="submitting">
            <span v-if="submitting" class="spinner"></span>
            {{ submitting ? $t('common.saving') : $t('common.save') }}
          </button>
        </div>
      </form>
    </Modal>
  </template>

<script setup lang="ts">
// @ts-ignore
import { ref as vueRef, computed as vueComputed, watch as vueWatch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useNotifications } from '@/composables/useNotifications'
import Modal from '@/components/base/Modal.vue'
import projectPhasesService from '@/services/projectPhases.service'

// Aliases to avoid TypeScript errors
const ref = vueRef
const computed = vueComputed
const watch = vueWatch

interface Phase {
  id?: number
  projectId: number
  name: string
  description?: string
  plannedStartDate?: string
  plannedEndDate?: string
}

const props = defineProps<{
  show: boolean
  phase: Phase | null
  projectId: number
}>()

const emit = defineEmits<{
  close: []
  saved: []
}>()

const { t } = useI18n()
const { notifySuccess, notifyError } = useNotifications()

// Form state
const form = ref<Phase>({
  projectId: props.projectId,
  name: '',
  description: '',
  plannedStartDate: '',
  plannedEndDate: ''
})

const errors = ref<Record<string, string>>({})
const submitting = ref(false)

// Computed
const isEditing = computed(() => !!props.phase?.id)

// Methods
const validateForm = (): boolean => {
  errors.value = {}
  
  if (!form.value.name || form.value.name.trim().length < 3) {
    errors.value.name = t('workspace.modal.errors.nameRequired')
    return false
  }
  
  if (form.value.plannedStartDate && form.value.plannedEndDate) {
    if (new Date(form.value.plannedEndDate) < new Date(form.value.plannedStartDate)) {
      errors.value.endDate = t('workspace.modal.errors.endDateBeforeStart')
      return false
    }
  }
  
  return true
}

const handleSubmit = async () => {
  if (!validateForm()) return
  
  submitting.value = true
  
  try {
    const phaseData = {
      ...form.value,
      projectId: props.projectId
    }
    
    let savedPhaseId: number
    
    if (isEditing.value) {
      await projectPhasesService.updatePhase(
        props.projectId.toString(),
        props.phase!.id!.toString(),
        phaseData
      )
      savedPhaseId = props.phase!.id!
    } else {
      const response = await projectPhasesService.createPhase(
        props.projectId.toString(),
        phaseData
      )
      savedPhaseId = response.id
    }
    
    notifySuccess(t(isEditing.value ? 'workspace.success.phaseUpdated' : 'workspace.success.phaseCreated'))
    emit('saved')
    closeModal()
  } catch (error) {
    notifyError(t('workspace.errors.phaseSaveFailed'))
  } finally {
    submitting.value = false
  }
}

const closeModal = () => {
  form.value = {
    projectId: props.projectId,
    name: '',
    description: '',
    plannedStartDate: '',
    plannedEndDate: ''
  }
  errors.value = {}
  emit('close')
}

// Watch for phase changes (editing mode)
watch(() => props.phase, (newPhase: Phase | null) => {
  if (newPhase) {
    form.value = {
      ...newPhase,
      projectId: props.projectId
    }
  } else {
    form.value = {
      projectId: props.projectId,
      name: '',
      description: '',
      plannedStartDate: '',
      plannedEndDate: ''
    }
  }
}, { immediate: true })
</script>

<style scoped>
/* Form Container */
.phase-form {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.form-group {
  margin-bottom: 1.5rem;
  padding: 0 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--neutral-700);
}

.form-group label.required::after {
  content: ' *';
  color: var(--error-600);
}

.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--neutral-300);
  border-radius: 8px;
  font-size: 0.875rem;
  transition: border-color 0.15s;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: var(--primary-500);
  box-shadow: 0 0 0 3px var(--primary-100);
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  padding: 0 1.5rem;
  margin-bottom: 1.5rem;
}

.form-row .form-group {
  margin-bottom: 0;
  padding: 0;
}

.error-message {
  display: block;
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: var(--error-600);
}

/* Form Actions */
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid var(--neutral-200);
  background: var(--neutral-50);
  margin-top: auto;
}

.btn {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  border: none;
  font-size: 0.875rem;
}

.btn-secondary {
  background: var(--neutral-100);
  color: var(--neutral-700);
}

.btn-secondary:hover {
  background: var(--neutral-200);
}

.btn-primary {
  background: var(--primary-600);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: var(--primary-700);
}

.btn-primary:disabled {
  background: var(--neutral-300);
  color: var(--neutral-500);
  cursor: not-allowed;
}

.spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  margin-right: 0.5rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
